import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BookOpen, CheckCircle, ArrowLeft, ArrowRight, Play,
  Clock, Globe, Scale, Target, MessageCircle, ExternalLink,
  ChevronRight, Compass, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const moduleIcons = {
  M1: Compass,
  M2: Globe,
  M3: Scale,
  M4: Target
};

const moduleColors = {
  M1: 'from-amber-500 to-orange-600',
  M2: 'from-emerald-500 to-teal-600',
  M3: 'from-blue-500 to-indigo-600',
  M4: 'from-purple-500 to-violet-600'
};

export default function FaithCurriculum() {
  const { moduleId } = useParams();
  const { user } = useAuth();
  
  const [curriculum, setCurriculum] = useState(null);
  const [currentModule, setCurrentModule] = useState(null);
  const [progress, setProgress] = useState({ completed_modules: [] });
  const [reflection, setReflection] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      // Fetch curriculum
      const currRes = await fetch(`${API}/api/faith/curriculum`);
      if (currRes.ok) {
        setCurriculum(await currRes.json());
      }

      // Fetch progress if logged in
      if (user) {
        const token = localStorage.getItem('token');
        const progRes = await fetch(`${API}/api/faith/curriculum/progress`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (progRes.ok) {
          setProgress(await progRes.json());
        }
      }
    } catch (err) {
      console.error('Error fetching curriculum:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const fetchModule = useCallback(async (id) => {
    try {
      const res = await fetch(`${API}/api/faith/curriculum/${id}`);
      if (res.ok) {
        setCurrentModule(await res.json());
      }
    } catch (err) {
      console.error('Error fetching module:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (moduleId) {
      fetchModule(moduleId);
    } else {
      setCurrentModule(null);
    }
  }, [moduleId, fetchModule]);

  const handleCompleteModule = async () => {
    if (!user) {
      toast.error('Please log in to track progress');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/faith/curriculum/progress`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          module_id: moduleId,
          completed: true,
          reflection_response: reflection || null
        })
      });

      if (res.ok) {
        setProgress(prev => ({
          ...prev,
          completed_modules: [...(prev.completed_modules || []), moduleId]
        }));
        toast.success('Module completed!');
      }
    } catch (err) {
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const isModuleComplete = (id) => progress.completed_modules?.includes(id);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600" />
      </div>
    );
  }

  // Module detail view
  if (currentModule) {
    const ModuleIcon = moduleIcons[currentModule.id] || BookOpen;
    const moduleColor = moduleColors[currentModule.id] || 'from-amber-500 to-orange-600';
    const isComplete = isModuleComplete(currentModule.id);
    const moduleIndex = curriculum?.modules?.findIndex(m => m.id === currentModule.id) || 0;
    const nextModule = curriculum?.modules?.[moduleIndex + 1];
    const prevModule = curriculum?.modules?.[moduleIndex - 1];

    return (
      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-amber-50/30" data-testid="module-viewer">
        {/* Header */}
        <div className={`bg-gradient-to-br ${moduleColor} text-white py-12`}>
          <div className="container mx-auto px-4">
            <Link to="/faith/curriculum" className="inline-flex items-center text-white/80 hover:text-white mb-6">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Curriculum
            </Link>
            
            <div className="flex items-start gap-4">
              <div className="p-4 bg-white/20 rounded-xl">
                <ModuleIcon className="h-10 w-10" />
              </div>
              <div>
                <Badge className="mb-2 bg-white/20 text-white border-0">Week {currentModule.week} of 4</Badge>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">{currentModule.title}</h1>
                {isComplete && (
                  <Badge className="bg-green-500 text-white">
                    <CheckCircle className="h-3 w-3 mr-1" /> Completed
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Global View */}
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center gap-2 text-amber-600 mb-2">
                <Globe className="h-5 w-5" />
                <span className="font-medium">The Global View</span>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-stone-700 text-lg leading-relaxed whitespace-pre-line">
                {currentModule.global_view}
              </p>
            </CardContent>
          </Card>

          {/* Ethical Pillar */}
          <Card className="mb-8 bg-stone-800 text-white">
            <CardHeader>
              <div className="flex items-center gap-2 text-amber-400 mb-2">
                <Scale className="h-5 w-5" />
                <span className="font-medium">Ethical Pillar</span>
              </div>
              <CardTitle className="text-2xl">{currentModule.ethical_pillar.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-stone-300 text-lg">{currentModule.ethical_pillar.description}</p>
            </CardContent>
          </Card>

          {/* Workday Practice */}
          <Card className="mb-8 border-2 border-amber-200 bg-amber-50/50">
            <CardHeader>
              <div className="flex items-center gap-2 text-amber-600 mb-2">
                <Heart className="h-5 w-5" />
                <span className="font-medium">Workday Practice</span>
              </div>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl text-stone-800">{currentModule.workday_practice.name}</CardTitle>
                <Badge variant="outline" className="text-amber-700 border-amber-300">
                  <Clock className="h-3 w-3 mr-1" /> {currentModule.workday_practice.duration_minutes} min
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-stone-700">{currentModule.workday_practice.instructions}</p>
              
              <div className="p-3 bg-white rounded-lg border border-amber-200">
                <p className="text-sm text-stone-500 mb-1">Short version (60 sec):</p>
                <p className="text-stone-700 italic">{currentModule.workday_practice.short_version}</p>
              </div>
            </CardContent>
          </Card>

          {/* Reflection Question */}
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center gap-2 text-amber-600 mb-2">
                <MessageCircle className="h-5 w-5" />
                <span className="font-medium">Reflection Question</span>
              </div>
              <CardTitle className="text-xl text-stone-800">
                {currentModule.reflection_question}
              </CardTitle>
            </CardHeader>
            {user && !isComplete && (
              <CardContent>
                <Textarea
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  placeholder="Take a moment to reflect... (optional)"
                  className="min-h-[120px] mb-4"
                />
                <Button 
                  onClick={handleCompleteModule}
                  disabled={saving}
                  className="bg-amber-600 hover:bg-amber-700"
                >
                  {saving ? 'Saving...' : 'Complete This Module'}
                  <CheckCircle className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            )}
          </Card>

          {/* Resources */}
          {currentModule.resources?.length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-lg">Dig Deeper</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {currentModule.resources.map((resource, idx) => (
                    <a
                      key={`item-${idx}`}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-stone-100 transition-colors"
                    >
                      <span className="text-stone-700">{resource.title}</span>
                      <ExternalLink className="h-4 w-4 text-stone-400" />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex justify-between">
            {prevModule ? (
              <Link to={`/faith/curriculum/${prevModule.id}`}>
                <Button variant="outline">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Week {prevModule.week}
                </Button>
              </Link>
            ) : <div />}
            
            {nextModule ? (
              <Link to={`/faith/curriculum/${nextModule.id}`}>
                <Button className="bg-amber-600 hover:bg-amber-700">
                  Week {nextModule.week} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            ) : (
              <Link to="/faith">
                <Button className="bg-green-600 hover:bg-green-700">
                  Complete Journey <CheckCircle className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Curriculum overview
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-amber-50/30" data-testid="faith-curriculum">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link to="/faith" className="inline-flex items-center text-amber-600 hover:text-amber-700 mb-8">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Faith Hub
        </Link>

        <div className="text-center mb-12">
          <Badge className="mb-4 bg-amber-100 text-amber-800 border-amber-200">
            <BookOpen className="h-3 w-3 mr-1" /> 4-Week Curriculum
          </Badge>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
            The Foundation Journey
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Four weeks. Four pillars. Identity, Community, Stewardship, Resilience—
            the spiritual backbone for anyone building something meaningful.
          </p>
        </div>

        {/* Progress */}
        {user && (
          <Card className="mb-8 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
            <CardContent className="py-6">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium">Your Progress</span>
                <span className="text-2xl font-bold">{progress.completion_percentage || 0}%</span>
              </div>
              <Progress value={progress.completion_percentage || 0} className="h-3 bg-white/30" />
              <p className="text-sm mt-2 text-white/80">
                {progress.completed_modules?.length || 0} of 4 modules completed
              </p>
            </CardContent>
          </Card>
        )}

        {/* Modules */}
        <div className="space-y-4">
          {curriculum?.modules?.map((module, idx) => {
            const ModuleIcon = moduleIcons[module.id] || BookOpen;
            const isComplete = isModuleComplete(module.id);
            const moduleColor = moduleColors[module.id];

            return (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link to={`/faith/curriculum/${module.id}`}>
                  <Card className={`hover:shadow-lg transition-all cursor-pointer border-2 ${
                    isComplete ? 'border-green-300 bg-green-50/50' : 'border-transparent hover:border-amber-200'
                  }`}>
                    <CardContent className="py-6">
                      <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl bg-gradient-to-br ${moduleColor} text-white`}>
                          <ModuleIcon className="h-6 w-6" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm text-stone-500">Week {module.week}</span>
                            {isComplete && (
                              <Badge className="bg-green-100 text-green-700 text-xs">
                                <CheckCircle className="h-3 w-3 mr-1" /> Done
                              </Badge>
                            )}
                          </div>
                          <h3 className="text-lg font-semibold text-stone-800">{module.title}</h3>
                          <p className="text-sm text-stone-500">
                            {module.ethical_pillar} • {module.practice_duration} min practice
                          </p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-stone-400" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {!user && (
          <Card className="mt-8 bg-stone-100 border-stone-200">
            <CardContent className="py-6 text-center">
              <p className="text-stone-600 mb-4">Log in to track your progress and save reflections.</p>
              <Link to="/login">
                <Button variant="outline">Log In</Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
